import { Group, MultiSelect, MultiSelectProps } from '@mantine/core';
import { useMemo } from 'react';

import UserAvatar from 'src/Components/NavBar/UserAvatar';
import useFetchAllMembers from 'src/Hooks/Queries/useFetchAllMembers';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';

import styles from './MemberSelector.module.scss';
import { Props } from './MemberSelector.types';

const MemberSelector = ({ allAssets = false, excludedMemberIds, ...props }: Props) => {
  const { data: currentAssetMembersData, isLoading: isLoadingCurrentAsset } = useFetchMembers();
  const { data: allMembersData, isLoading: isLoadingAllMembers } = useFetchAllMembers();

  const membersData = allAssets ? allMembersData : currentAssetMembersData;
  const isLoading = allAssets ? isLoadingAllMembers : isLoadingCurrentAsset;

  const memberOptions = useMemo(() => {
    if (!membersData) return [];

    const excluded = new Set(excludedMemberIds ?? []);
    const seen = new Set<string>();
    return membersData.members.reduce<{ value: string; label: string }[]>((acc, member) => {
      if (seen.has(member.user.id) || excluded.has(member.user.id)) return acc;
      seen.add(member.user.id);
      acc.push({
        value: member.user.id,
        label: `${member.user.firstName} ${member.user.lastName}`,
      });
      return acc;
    }, []);
  }, [membersData, excludedMemberIds]);

  const renderMultiSelectOption: MultiSelectProps['renderOption'] = ({ option }) => (
    <Group gap="sm">
      <UserAvatar user={membersData?.members.find(m => m.user.id === option.value)?.user} />
      <div>
        <div className={styles.name}>{option.label}</div>
        <div className={styles.email}>
          {membersData?.members.find(m => m.user.id === option.value)?.user.email}
        </div>
      </div>
    </Group>
  );

  return (
    <MultiSelect
      data={memberOptions}
      renderOption={renderMultiSelectOption}
      disabled={isLoading}
      hidePickedOptions
      clearable
      searchable
      classNames={{
        wrapper: styles.wrapper,
        input: styles.input,
        pillsList: styles.pillsList,
      }}
      {...props}
    />
  );
};

export default MemberSelector;
